import { useMutation } from '@tanstack/react-query';
import { announceSavedOffline } from '@/stores/savedOffline';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import { StorePicker } from '@/components/StorePicker';
import { useState } from 'react';
import type { components } from '@/api/schema';
import {
  DialogSubject,
  ErrorNote,
  Field,
  FormActions,
  Modal,
  Select,
  errorMessage,
  quantity,
  todayIso,
} from '@/components/ui';
import { useBatches, useItemsById, useStockInvalidation, useStores } from '@/hooks/inventory';
import { PackedQtyInput } from '@/components/PackedQtyInput';
import { packedBreakdown } from '@/packing';
import { KEPT_ON_THIS_PC, saveDocument } from '@/hooks/stockSend';
import { useAuth } from '@/stores/auth';
import { VehicleField } from '@/components/VehicleField';

type StockRow = components['schemas']['StockBalanceItemDto'];

export type BulkActionKind = 'transfer' | 'issue' | 'adjust';

const TITLES: Record<BulkActionKind, string> = {
  transfer: 'Transfer selected items',
  issue: 'Issue selected items',
  adjust: 'Adjust selected items',
};

const ICON: Record<BulkActionKind, IconKind> = {
  transfer: 'transfer',
  issue: 'outbound',
  adjust: 'adjust',
};

interface Line {
  qty: string;
  batchId: string;
}

/** A lot picker for a row that named no specific lot — the same rule the
 *  single-item quick actions use, just per line here. */
function LotCell({
  row,
  batchId,
  onChange,
}: {
  row: StockRow;
  batchId: string;
  onChange: (batchId: string) => void;
}) {
  const needsChoice = row.isBatchTracked && !row.batchId;
  const { data: batches } = useBatches(row.storeId, row.skuId, needsChoice);

  if (!needsChoice) return <span className="dim">{row.batchNo ?? '—'}</span>;

  return (
    <select
      className="field__input"
      aria-label={`Lot for ${row.skuCode}`}
      required
      value={batchId}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Choose a lot…</option>
      {(batches ?? []).map((b) => (
        <option key={b.id} value={b.id}>
          {b.batchNo} — {quantity(b.qty, row.uomPrecision)} on hand
        </option>
      ))}
    </select>
  );
}

/**
 * One dialog acting on every row checked in the Stock grid at once — moving,
 * issuing or adjusting several items in the time a single quick action would
 * take for one.
 *
 * Transfer and Adjust post one document with a line per item, which is what
 * the server already accepts; each document names one store, so every row
 * has to come from (or belong to) the same store, or the dialog says so
 * instead of guessing which store the document should carry. Issue posts one
 * document per item, since a consumption document has always held exactly
 * one — rows can span stores freely there.
 */
export function BulkActionDialog({
  action,
  rows,
  onClose,
  onSaved,
}: {
  action: BulkActionKind;
  rows: StockRow[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const singleStoreNeeded = action === 'transfer' || action === 'adjust';
  const storeIds = new Set(rows.map((r) => r.storeId));
  const mixedStores = singleStoreNeeded && storeIds.size > 1;

  return (
    <Modal title={TITLES[action]} onClose={onClose} wide>
      <h2 className="withicon">
        <AppIcon kind={ICON[action]} size={18} />
        {TITLES[action]}
      </h2>
      <DialogSubject
        icon="stock"
        name={`${rows.length} items selected`}
        tags={[...new Set(rows.map((r) => r.storeCode))].map((code) => ({ icon: 'store' as const, label: code }))}
      />

      {mixedStores ? (
        <>
          <ErrorNote
            message={`These items are held in ${storeIds.size} different stores. A ${action === 'transfer' ? 'transfer' : 'adjustment'} document belongs to one store — narrow the selection to items from a single store first.`}
          />
          <FormActions onCancel={onClose} cancelLabel="Close">
            <span />
          </FormActions>
        </>
      ) : action === 'transfer' ? (
        <BulkTransferFields rows={rows} onClose={onClose} onSaved={onSaved} />
      ) : action === 'issue' ? (
        <BulkIssueFields rows={rows} onClose={onClose} onSaved={onSaved} />
      ) : (
        <BulkAdjustFields rows={rows} onClose={onClose} onSaved={onSaved} />
      )}
    </Modal>
  );
}

function initialLines(rows: StockRow[]): Map<string, Line> {
  return new Map(rows.map((r) => [r.id, { qty: r.qty, batchId: r.batchId ?? '' }]));
}

function LinesTable({
  rows,
  lines,
  setQty,
  setBatchId,
  overCapId,
}: {
  rows: StockRow[];
  lines: Map<string, Line>;
  setQty: (id: string, qty: string) => void;
  setBatchId: (id: string, batchId: string) => void;
  overCapId: (row: StockRow, qty: string) => boolean;
}) {
  const items = useItemsById();
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Item</th>
          <th>Store</th>
          <th className="num">On hand</th>
          <th>Lot</th>
          <th className="num">Quantity</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const line = lines.get(row.id)!;
          const over = overCapId(row, line.qty);
          return (
            <tr key={row.id}>
              <td>
                <span className="mono">{row.skuCode}</span> {row.skuName}
              </td>
              <td className="dim">{row.storeCode}</td>
              <td className="num">
                {quantity(row.qty, row.uomPrecision)}
                {items.get(row.skuId) && packedBreakdown(row.qty, items.get(row.skuId)!) && (
                  <div className="dim">{packedBreakdown(row.qty, items.get(row.skuId)!)}</div>
                )}
              </td>
              <td>
                <LotCell row={row} batchId={line.batchId} onChange={(v) => setBatchId(row.id, v)} />
              </td>
              <td>
                <PackedQtyInput
                  label="Quantity"
                  srLabel={` for ${row.skuCode}`}
                  labelClassName="sr-only"
                  item={items.get(row.skuId)}
                  itemKey={row.skuId}
                  remember="draw"
                  value={line.qty}
                  onChange={(qty) => setQty(row.id, qty)}
                />
                {over && (
                  <div className="errornote">
                    <span aria-hidden="true">⚠ </span>more than the {quantity(row.qty, row.uomPrecision)} held
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ------------------------------------------------------------- transfer

function BulkTransferFields({
  rows,
  onClose,
  onSaved,
}: {
  rows: StockRow[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const { data: stores } = useStores();
  const fromStoreId = rows[0]!.storeId;

  const [toStoreId, setToStoreId] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [isReturn, setIsReturn] = useState(false);
  const [lines, setLines] = useState(() => initialLines(rows));
  const [problem, setProblem] = useState<string | null>(null);

  const canPost = can('inv.transfer.post');
  const overCap = (row: StockRow, qty: string) => qty !== '' && Number(qty) > Number(row.qty);


  const save = useMutation({
    mutationFn: async (post: boolean) => {
      const body = {
        fromStoreId,
        toStoreId,
        transferDate: todayIso(),
        isReturn,
        vehicleNo: vehicleNo.trim() || undefined,
        lines: rows.map((row) => {
          const line = lines.get(row.id)!;
          return { skuId: row.skuId, qty: line.qty, ...(line.batchId ? { batchId: line.batchId } : {}) };
        }),
      };
      const doc = await saveDocument({
        kind: 'transfer',
        path: '/api/v1/inventory/transfers',
        body,
        post,
        describes: `Transfer ${todayIso()}: ${rows.length} item(s) from ${rows[0]!.storeCode}`,
        screen: 'inv-xfer',
      });
      return { ...doc, posted: post };
    },
    onSuccess: (result) => {
      invalidate();
      onSaved(
        result.warning ?? (result.waiting ? KEPT_ON_THIS_PC : result.posted
          ? `${result.documentNo} posted. ${rows.length} items moved from ${rows[0]!.storeCode}.`
          : `${result.documentNo} saved as a draft. Someone with posting rights must post it before stock changes.`),
      );
    },
  });

  function submit(post: boolean) {
    if (!toStoreId) return setProblem('Choose the destination store.');
    if (toStoreId === fromStoreId) return setProblem('A store cannot send stock to itself.');
    for (const row of rows) {
      const line = lines.get(row.id)!;
      if (!(Number(line.qty) > 0)) return setProblem(`Enter a quantity for ${row.skuCode}.`);
      if (overCap(row, line.qty)) return setProblem(`Only ${quantity(row.qty, row.uomPrecision)} of ${row.skuCode} is held here.`);
      if (row.isBatchTracked && !row.batchId && !line.batchId) return setProblem(`Choose which lot of ${row.skuCode} this is moved from.`);
    }
    setProblem(null);
    save.mutate(post);
  }

  const storeOptions = stores?.filter((s) => s.id !== fromStoreId && s.isActive);

  return (
    <div className="stack">
      <p className="muted formgroup__hint">
        From <strong>{rows[0]!.storeCode}</strong>
      </p>
      <div className="formgrid formgrid--3">
        <StorePicker label="To store" required value={toStoreId} onChange={setToStoreId} stores={storeOptions} span={2} />
        <VehicleField label="Vehicle" value={vehicleNo} onChange={setVehicleNo} placeholder="Registration number" />
        <label className="settings__toggle">
          <input type="checkbox" checked={isReturn} onChange={(e) => setIsReturn(e.target.checked)} />
          <span>This is a return</span>
        </label>
      </div>

      <LinesTable
        rows={rows}
        lines={lines}
        setQty={(id, qty) => setLines((prev) => new Map(prev).set(id, { ...prev.get(id)!, qty }))}
        setBatchId={(id, batchId) => setLines((prev) => new Map(prev).set(id, { ...prev.get(id)!, batchId }))}
        overCapId={overCap}
      />

      {problem && <ErrorNote message={problem} />}
      {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

      <FormActions onCancel={onClose}>
        {canPost ? (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(true)}>
            {save.isPending ? 'Saving…' : 'Move and post'}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => submit(false)}>
            {save.isPending ? 'Saving…' : 'Save draft'}
          </button>
        )}
      </FormActions>
    </div>
  );
}

// ---------------------------------------------------------------- issue

function BulkIssueFields({
  rows,
  onClose,
  onSaved,
}: {
  rows: StockRow[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const invalidate = useStockInvalidation();

  const [issuedTo, setIssuedTo] = useState('');
  const [lines, setLines] = useState(() => initialLines(rows));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * What has gone through, or is safe on this PC (.docs/023). Rows that did
   * leave the list, so saving again sends only what failed.
   */
  const [done, setDone] = useState<ReadonlySet<string>>(() => new Set());
  const left = rows.filter((row) => !done.has(row.id));

  const canPost = can('inv.consumption.post');
  const overCap = (row: StockRow, qty: string) => qty !== '' && Number(qty) > Number(row.qty);

  async function submit(post: boolean) {
    for (const row of left) {
      const line = lines.get(row.id)!;
      if (!(Number(line.qty) > 0)) return setProblem(`Enter a quantity for ${row.skuCode}.`);
      if (overCap(row, line.qty)) return setProblem(`Only ${quantity(row.qty, row.uomPrecision)} of ${row.skuCode} is held here.`);
      if (row.isBatchTracked && !row.batchId && !line.batchId) return setProblem(`Choose which lot of ${row.skuCode} this is issued from.`);
    }
    setProblem(null);
    setError(null);
    setSaving(true);

    const failed: string[] = [];
    const nowDone = new Set(done);
    let kept = 0;
    for (const row of left) {
      const line = lines.get(row.id)!;
      const body = {
        storeId: row.storeId,
        skuId: row.skuId,
        qty: line.qty,
        consumedOn: todayIso(),
        ...(line.batchId ? { batchId: line.batchId } : {}),
        issuedTo: issuedTo.trim() || undefined,
      };
      try {
        const doc = await saveDocument({
          kind: 'consumption',
          path: '/api/v1/inventory/consumption',
          body,
          post,
          describes: `Stock OUT ${todayIso()}: ${line.qty} of ${row.skuCode}`,
          screen: 'inv-consume',
          quiet: true,
        });
        if (doc.waiting) kept += 1;
        if (doc.warning) failed.push(`${row.skuCode} (${doc.warning})`);
        nowDone.add(row.id);
      } catch (e) {
        failed.push(`${row.skuCode} (${errorMessage(e, 'failed')})`);
      }
    }

    setSaving(false);
    setDone(nowDone);
    invalidate();
    if (kept > 0) {
      void announceSavedOffline({
        what: `Stock OUT ${todayIso()}: ${kept} ${kept === 1 ? 'item' : 'items'}`,
        closeLabel: 'OK',
        note: 'Each gets its document number when it is sent.',
      });
    }
    if (failed.length === 0) {
      onSaved(
        kept > 0
          ? `${nowDone.size - kept} sent, ${kept} ${KEPT_ON_THIS_PC.toLowerCase()}`
          : post
            ? `${nowDone.size} items issued and posted.`
            : `${nowDone.size} items saved as drafts. Someone with posting rights must post them before stock changes.`,
      );
      return;
    }
    setError(
      [
        `${nowDone.size} of ${rows.length} issued. Failed: ${failed.join(', ')}.`,
        nowDone.size > 0 ? 'The items that went through are off the list. Only what failed is left.' : '',
      ]
        .filter(Boolean)
        .join(' '),
    );
  }

  return (
    <div className="stack">
      <Field label="Issued to" value={issuedTo} onChange={setIssuedTo} placeholder="Shed 3, or a person's name" />

      <LinesTable
        rows={left}
        lines={lines}
        setQty={(id, qty) => setLines((prev) => new Map(prev).set(id, { ...prev.get(id)!, qty }))}
        setBatchId={(id, batchId) => setLines((prev) => new Map(prev).set(id, { ...prev.get(id)!, batchId }))}
        overCapId={overCap}
      />

      {problem && <ErrorNote message={problem} />}
      {error && <ErrorNote message={error} />}

      <FormActions onCancel={onClose}>
        {canPost ? (
          <button type="button" className="btn btn--primary" disabled={saving} onClick={() => submit(true)}>
            {saving ? 'Saving…' : 'Issue and post'}
          </button>
        ) : (
          <button type="button" className="btn btn--primary" disabled={saving} onClick={() => submit(false)}>
            {saving ? 'Saving…' : 'Save drafts'}
          </button>
        )}
      </FormActions>
    </div>
  );
}

// --------------------------------------------------------------- adjust

function BulkAdjustFields({
  rows,
  onClose,
  onSaved,
}: {
  rows: StockRow[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const invalidate = useStockInvalidation();
  const storeId = rows[0]!.storeId;

  const [direction, setDirection] = useState<'1' | '-1'>('1');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState(() => initialLines(rows));
  const [problem, setProblem] = useState<string | null>(null);

  const overCap = (row: StockRow, qty: string) => direction === '-1' && qty !== '' && Number(qty) > Number(row.qty);

  const save = useMutation({
    mutationFn: () =>
      saveDocument({ kind: 'adjustment', path: '/api/v1/inventory/adjustments', post: false, describes: `Adjustment ${todayIso()}: ${rows.length} item(s), ${reason.trim()}`, screen: 'inv-adjust', body: {
        storeId,
        adjustmentDate: todayIso(),
        reason: reason.trim(),
        lines: rows.map((row) => {
          const line = lines.get(row.id)!;
          return {
            skuId: row.skuId,
            qty: `${direction === '-1' ? '-' : ''}${line.qty}`,
            ...(line.batchId ? { batchId: line.batchId } : {}),
          };
        }),
      } }),
    onSuccess: (result) => {
      invalidate();
      onSaved(
        result.waiting
          ? KEPT_ON_THIS_PC
          : `${result.documentNo} raised for ${rows.length} items. Someone else must approve it before it can post.`,
      );
    },
  });

  function submit() {
    for (const row of rows) {
      const line = lines.get(row.id)!;
      if (!(Number(line.qty) > 0)) return setProblem(`Enter a quantity for ${row.skuCode}.`);
      if (overCap(row, line.qty)) return setProblem(`Only ${quantity(row.qty, row.uomPrecision)} of ${row.skuCode} is held here to write off.`);
      if (row.isBatchTracked && !row.batchId && !line.batchId) return setProblem(`Choose which lot of ${row.skuCode} this adjustment is for.`);
    }
    if (!reason.trim()) return setProblem('Say why this adjustment is needed — it is the whole audit trail.');
    setProblem(null);
    save.mutate();
  }

  return (
    <div className="stack">
      <p className="muted formgroup__hint">
        In <strong>{rows[0]!.storeCode}</strong>
      </p>
      <div className="formgrid formgrid--2">
        <Select
          label="Direction"
          required
          value={direction}
          onChange={(v) => setDirection(v as '1' | '-1')}
          options={[
            { value: '1', label: 'Increase (found more than recorded)' },
            { value: '-1', label: 'Decrease (write off — damage, spillage, miscount)' },
          ]}
          placeholder=""
        />
        <Field label="Reason" required value={reason} onChange={setReason} placeholder="Damaged in the store, spillage during handling…" />
      </div>

      <LinesTable
        rows={rows}
        lines={lines}
        setQty={(id, qty) => setLines((prev) => new Map(prev).set(id, { ...prev.get(id)!, qty }))}
        setBatchId={(id, batchId) => setLines((prev) => new Map(prev).set(id, { ...prev.get(id)!, batchId }))}
        overCapId={overCap}
      />

      {problem && <ErrorNote message={problem} />}
      {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

      <FormActions onCancel={onClose}>
        <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={submit}>
          {save.isPending ? 'Saving…' : 'Raise adjustment'}
        </button>
      </FormActions>
    </div>
  );
}
